import axios, { type AxiosError, type AxiosInstance, type AxiosRequestConfig } from 'axios';
import { emitClearSession, emitTokenUpdate } from '../hook_au/authEvents';

let accessToken: string | null = null;
let refreshPromise: Promise<string | null> | null = null;

export const setAccessToken = (token: string | null) => {
  accessToken = token;
};

const apiClient: AxiosInstance = axios.create({
  baseURL: '/api',
  withCredentials: true,
});

apiClient.interceptors.request.use((config) => {
  if (accessToken && !config.headers?.Authorization) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

const refreshAccessToken = async (): Promise<string | null> => {
  if (!refreshPromise) {
    refreshPromise = apiClient
      .post('/auth/refresh', undefined, { _retry: true } as AxiosRequestConfig)
      .then((response) => response.data?.access_token as string)
      .catch(() => null)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const status = error.response?.status;
    const originalConfig = error.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined;
    const isRefreshRequest = originalConfig?.url?.includes('/auth/refresh');

    if (status === 401 && originalConfig && !originalConfig._retry && !isRefreshRequest) {
      originalConfig._retry = true;
      const newToken = await refreshAccessToken();
      if (newToken) {
        setAccessToken(newToken);
        emitTokenUpdate(newToken);
        originalConfig.headers = { ...(originalConfig.headers ?? {}), Authorization: `Bearer ${newToken}` };
        return apiClient.request(originalConfig);
      }
      emitClearSession();
      window.location.replace('/login');
    }

    return Promise.reject(error);
  },
);

export default apiClient;
